import {
  Autocomplete,
  AutocompleteProps,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { CatalogProduct } from '../types'
import { useEffect, useState } from 'react'
import { getCatalogProducts } from '../services/catalogProductService'
import { useTranslation } from 'react-i18next'
import { Circle } from '@mui/icons-material'
import { usePolicy } from '../hooks'
import { CatalogProductStatus } from '../enums'

type CatalogProductGroupBy = 'manufacturer' | 'fuse' | 'heatingPower'

export interface CatalogProductAutocompleteProps
  extends Omit<
    AutocompleteProps<CatalogProduct, false, false, false>,
    'options' | 'value' | 'renderInput' | 'groupBy'
  > {
  value?: string | null
  label?: string
  placeholder?: string
  options?: CatalogProduct[]
  groupBy?: CatalogProductGroupBy
}

export default function CatalogProductAutocomplete({
  value,
  label,
  placeholder,
  options,
  groupBy,
  ...props
}: CatalogProductAutocompleteProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The policy state.
   */
  const { isSuperadmin, isManufacturer } = usePolicy()

  /**
   * The catalog products state.
   */
  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([])

  /**
   * The sorted options.
   */
  const sortedOptions = sortOptions(catalogProducts)

  /**
   * Sort the options.
   */
  function sortOptions(options: CatalogProduct[]) {
    return [...options].sort((a, b) => {
      if (groupBy === 'manufacturer') {
        return a.manufacturer.localeCompare(b.manufacturer)
      } else if (groupBy === 'fuse') {
        return (a.customData?.fuse ?? 0) - (b.customData?.fuse ?? 0)
      } else if (groupBy === 'heatingPower') {
        return (a.customData?.heatingPower ?? 0) - (b.customData?.heatingPower ?? 0)
      }

      return a.name.localeCompare(b.name)
    })
  }

  function groupByOption(option: CatalogProduct) {
    if (groupBy === 'fuse') {
      return `${option?.customData?.fuse}A`
    } else if (groupBy === 'heatingPower') {
      return `${option?.customData?.heatingPower} kW`
    } else {
      return option.manufacturer
    }
  }

  /**
   * Load the catalog products.
   */
  async function loadCatalogProducts(controller?: AbortController) {
    try {
      setCatalogProducts(
        await getCatalogProducts(
          {
            status: isSuperadmin || isManufacturer ? undefined : CatalogProductStatus.VERIFIED
          },
          { signal: controller?.signal }
        )
      )
    } catch (_) {
      // Do nothing.
    }
  }

  /**
   * Load the catalog products when the component mounts unless options are provided.
   */
  useEffect(() => {
    if (options === undefined) {
      const controller = new AbortController()

      loadCatalogProducts(controller)

      return () => controller.abort()
    } else {
      setCatalogProducts(options)
    }
  }, [options])

  return (
    <Autocomplete
      options={sortedOptions}
      getOptionLabel={(option) => option.name}
      getOptionKey={(option) => option.id}
      groupBy={groupBy ? groupByOption : undefined}
      inputValue={value ?? undefined}
      renderOption={(props, option) => (
        <MenuItem {...props}>
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography>
              {option.name} ({option.manufacturerProductCode})
            </Typography>
            <Circle color="disabled" sx={{ fontSize: '4px' }} />
            <Typography color="text.secondary">{option.manufacturer}</Typography>
          </Stack>
        </MenuItem>
      )}
      renderInput={(params) => (
        <TextField
          label={label ?? t('selectCatalogProduct')}
          placeholder={placeholder}
          {...params}
        />
      )}
      {...props}
    />
  )
}
